import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { Trade, TradeColor } from '@/types/scheduler';

type TradeStatus = 'active' | 'archived' | 'all';

export interface TradeInput {
  name: string;
  color?: TradeColor;
  icon?: string | null;
  sortOrder?: number;
}

export interface UpdateTradeInput {
  name?: string;
  color?: TradeColor;
  icon?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

/** All trades for the tenant (default: every status). */
export function useTrades(status: TradeStatus = 'all') {
  return useApiQuery<Trade[]>({
    queryKey: schedulerKeys.trades(status),
    queryFn: () =>
      apiGet<Trade[]>(operationsClient, schedulerEndpoints.trades(), {
        params: { status },
      }),
    showErrorToast: false,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  // A trade change (add/edit/remove) also moves the trade catalog's enabled set.
  return () => {
    void queryClient.invalidateQueries({
      queryKey: [...schedulerKeys.all(), 'trades'],
    });
    void queryClient.invalidateQueries({ queryKey: schedulerKeys.tradeCatalog() });
  };
}

export function useCreateTrade() {
  const invalidate = useInvalidate();
  return useApiMutation<Trade, TradeInput>({
    mutationFn: (dto) =>
      apiPost<Trade, TradeInput>(operationsClient, schedulerEndpoints.trades(), dto),
    onSuccess: invalidate,
    showErrorToast: true,
  });
}

export function useUpdateTrade() {
  const invalidate = useInvalidate();
  return useApiMutation<Trade, { id: string; dto: UpdateTradeInput }>({
    mutationFn: ({ id, dto }) =>
      apiPatch<Trade, UpdateTradeInput>(operationsClient, schedulerEndpoints.trade(id), dto),
    onSuccess: invalidate,
    showErrorToast: true,
  });
}

export function useDeleteTrade() {
  const invalidate = useInvalidate();
  return useApiMutation<void, string>({
    mutationFn: (id) => apiDelete<void>(operationsClient, schedulerEndpoints.trade(id)),
    onSuccess: invalidate,
    showErrorToast: true,
  });
}
